/**
 * @file Parse the `?s={sessionId}` query and `#k={transportKey}` fragment from
 * the export landing URL, then immediately strip the fragment from the browser
 * history so that the transport key never ends up in `window.history` or in a
 * referer header (export_security_v1 §3.4 対策 C).
 */

export interface ParsedExportUrl {
  sessionId: string;
  /** URL-safe base64 of the 32-byte transport key (padding `=` removed). */
  transportKeyB64Url: string;
}

export class InvalidExportUrlError extends Error {
  constructor(reason: string) {
    super(`invalid export url: ${reason}`);
    this.name = "InvalidExportUrlError";
  }
}

/**
 * Parse `location.search` + `location.hash`. Throws [InvalidExportUrlError] if
 * either `s` or `k` is missing. The caller is expected to call
 * [scrubTransportKeyFromHistory] on success to remove the fragment.
 */
export function parseExportLocation(loc: { search: string; hash: string }): ParsedExportUrl {
  const params = new URLSearchParams(loc.search);
  const sessionId = params.get("s");
  if (!sessionId) {
    throw new InvalidExportUrlError("missing query parameter `s`");
  }
  // hash is e.g. "#k=AbCd...". URLSearchParams doesn't take it directly.
  const hash = loc.hash.startsWith("#") ? loc.hash.slice(1) : loc.hash;
  const hashParams = new URLSearchParams(hash);
  const k = hashParams.get("k");
  if (!k) {
    throw new InvalidExportUrlError("missing fragment parameter `#k`");
  }
  return { sessionId, transportKeyB64Url: k };
}

/**
 * Replace the current URL with one that has no `#k=...` fragment. Must be
 * called as soon as we've extracted the transport key, so the fragment doesn't
 * sit in the browser history (history is the only place fragments leak).
 *
 * The query string (`?s=...`) is kept so reload still loads the same session.
 */
export function scrubTransportKeyFromHistory(): void {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  const cleanUrl = window.location.pathname + window.location.search;
  window.history.replaceState(null, "", cleanUrl);
}

/** URL-safe base64 → Uint8Array (handles missing padding). */
export function decodeBase64Url(b64url: string): Uint8Array {
  const padded = b64url
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(b64url.length + ((4 - (b64url.length % 4)) % 4), "=");
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Standard base64 → Uint8Array. */
export function decodeBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Uint8Array → standard base64 (with padding). */
export function encodeBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}
